/**
 * NotazzIntegrationCard - Card para tela de integracoes
 *
 * Card simples que navega para a pagina de integracao Notazz
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../../config/api';

const NotazzIntegrationCard = () => {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadNotazzStatus();
  }, []);

  const loadNotazzStatus = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token');

      const configResponse = await fetch(`${API_URL}/api/notazz/config`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (configResponse.ok) {
        const configResult = await configResponse.json();
        setConfig(configResult.data);
      }

      const statsResponse = await fetch(`${API_URL}/api/notazz/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (statsResponse.ok) {
        const statsResult = await statsResponse.json();
        setStats(statsResult.stats);
      }
    } catch (error) {
      console.error('Erro ao carregar status Notazz:', error);
    } finally {
      setLoading(false);
    }
  };

  const getIntegrationStatus = () => {
    if (!config) {
      return {
        status: 'not-configured',
        label: 'Nao Configurado',
        color: 'secondary',
        icon: 'fa-exclamation-circle'
      };
    }

    if (!config.isActive) {
      return {
        status: 'inactive',
        label: 'Inativo',
        color: 'warning',
        icon: 'fa-pause-circle'
      };
    }

    if (stats && stats.totalNotas > 0) {
      const successRate = parseFloat(stats.successRate);
      if (successRate >= 95) {
        return {
          status: 'excellent',
          label: 'Excelente',
          color: 'success',
          icon: 'fa-check-circle'
        };
      } else if (successRate >= 80) {
        return {
          status: 'good',
          label: 'Bom',
          color: 'info',
          icon: 'fa-check-circle'
        };
      } else {
        return {
          status: 'needs-attention',
          label: 'Atencao',
          color: 'warning',
          icon: 'fa-exclamation-triangle'
        };
      }
    }

    return {
      status: 'configured',
      label: 'Configurado',
      color: 'primary',
      icon: 'fa-cog'
    };
  };

  const integrationStatus = getIntegrationStatus();

  return (
    <div className="card h-100">
      <div className="card-body">
        <div className="d-flex align-items-start mb-3">
          <div className="flex-shrink-0">
            <div className={`bg-${integrationStatus.color} bg-opacity-10 rounded-circle p-3`}>
              <i className={`fas ${integrationStatus.icon} fa-2x text-${integrationStatus.color}`}></i>
            </div>
          </div>
          <div className="flex-grow-1 ms-3">
            <span className="card-title mb-1" style={{ fontSize: '18px', fontWeight: '600', display: 'block' }}>
              <i className="fas fa-file-invoice me-2"></i>
              Notazz
            </span>
            <p className="card-text text-muted mb-2">
              Emissao automatica de notas fiscais (NF-e e NFS-e)
            </p>
            <span className={`badge bg-${integrationStatus.color}`}>
              {integrationStatus.label}
            </span>
          </div>
        </div>

        {stats && stats.totalNotas > 0 && (
          <div className="row text-center mb-3">
            <div className="col-4">
              <div className="small text-muted">Total</div>
              <div className="fw-bold">{stats.totalNotas}</div>
            </div>
            <div className="col-4">
              <div className="small text-muted">Autorizadas</div>
              <div className="fw-bold text-success">{stats.autorizadas}</div>
            </div>
            <div className="col-4">
              <div className="small text-muted">Taxa</div>
              <div className="fw-bold text-info">{stats.successRate}%</div>
            </div>
          </div>
        )}

        <div className="mb-3">
          {config ? (
            <div className="small text-muted">
              <i className="fas fa-key me-1"></i>
              API Key: {config.apiKeyPreview}
              <br />
              <i className="fas fa-clock me-1"></i>
              Ultima atualizacao: {new Date(config.updatedAt).toLocaleDateString('pt-BR')}
            </div>
          ) : (
            <div className="small text-muted">
              <i className="fas fa-info-circle me-1"></i>
              Configure sua API Key do Notazz para comecar
            </div>
          )}
        </div>

        <div className="d-grid">
          <button
            className={`btn btn-${config ? 'outline-primary' : 'primary'}`}
            onClick={() => navigate('/integration/notazz')}
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                Carregando...
              </>
            ) : (
              <>
                <i className={`fas ${config ? 'fa-cog' : 'fa-plus'} me-2`}></i>
                {config ? 'Gerenciar' : 'Configurar'} Integracao
              </>
            )}
          </button>
        </div>
      </div>

      <div className="card-footer bg-light">
        <div className="d-flex justify-content-between align-items-center">
          <div className="small text-muted">
            <i className="fas fa-file-invoice me-1"></i>
            NF-e e NFS-e automaticas
          </div>
          {stats && stats.pendentes > 0 && (
            <span className="badge bg-warning">
              {stats.pendentes} pendente{stats.pendentes > 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotazzIntegrationCard;
